import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Автообновление агентов (ТЗ §11, фаза 9, задача 4).
 *
 * Платформа знает, какая версия агента лежит у неё в каталоге выпусков,
 * и предлагает её агентам, у которых версия старее. Агент скачивает,
 * сверяет контрольную сумму, подменяет свой бинарник и завершается —
 * его перезапускает служба.
 *
 * Отключается на двух уровнях, и оба нужны. Со стороны платформы —
 * флагом у агента: администратор площадки может не хотеть обновления
 * в разгар работы. Со стороны машины — ключом `--no-auto-update`:
 * §1.3.1 не даёт платформе права распоряжаться чужим сервером без спроса,
 * и последнее слово остаётся за тем, кто этот сервер держит.
 */
/** Платформа сборки: `linux/amd64`, `windows/amd64`, `linux/arm64`. */
export const buildPlatformSchema = z
    .string()
    .regex(/^[a-z0-9]+\/[a-z0-9]+$/, messageKey('validation.agent.platform'))
    .max(32);
/**
 * Версия — три числа через точку.
 *
 * Без суффиксов вроде `-rc1`: сравнение версий должно быть однозначным,
 * а «1.2.0-rc1 старше или новее 1.2.0» — вопрос, на который у разных
 * реализаций разные ответы. Предвыпуски раздаются вручную.
 */
export const agentVersionSchema = z
    .string()
    .regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/, messageKey('validation.agent.version'))
    .max(16);
export const agentBuildSchema = z.object({
    /** Имя файла в каталоге выпусков. Пути здесь быть не может. */
    file: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[A-Za-z0-9._-]+$/, messageKey('validation.agent.fileName')),
    /** sha256 в hex. Агент сверяет её до подмены бинарника. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/, messageKey('validation.agent.checksum')),
    sizeBytes: z.number().int().positive(),
});
/**
 * Содержимое `releases.json` в каталоге выпусков.
 *
 * Файл, а не разбор имён: имена сборок у всех разные, а угадывание версии
 * из имени файла — способ однажды предложить площадке не тот бинарник.
 * Заодно контрольная сумма лежит рядом и в одном месте.
 */
export const agentReleaseManifestSchema = z.object({
    version: agentVersionSchema,
    builds: z.record(buildPlatformSchema, agentBuildSchema),
    /**
     * Скрипты установки, ключ — семейство ОС (`linux`, `windows`).
     *
     * Лежат рядом со сборками и раздаются тем же порядком: администратору
     * площадки нужен не бинарник сам по себе, а работающая служба, а между
     * первым и вторым стоит ровно этот скрипт. Поле необязательное — каталоги,
     * собранные до появления раздачи, остаются читаемыми.
     */
    scripts: z
        .record(z
        .string()
        .regex(/^[a-z0-9]+$/, messageKey('validation.agent.platform'))
        .max(32), agentBuildSchema)
        .default({}),
    /** Что изменилось — показывается администратору перед обновлением. */
    notes: z.string().max(2000).default(''),
});
/**
 * Что можно скачать из каталога выпусков.
 *
 * Одним списком и сборки, и скрипты установки: на экране они стоят рядом
 * и качаются одинаково, а разделены полем `kind` — оно решает, как строку
 * подписать.
 */
export const agentAssetSchema = z.object({
    /**
     * Ключ скачивания: `linux/amd64` у сборки, `script/linux` у установщика.
     *
     * Он же — единственное, что приходит от браузера при скачивании. Имя файла
     * платформа берёт из манифеста по этому ключу, а не из запроса: путь,
     * пришедший снаружи, до файловой системы не доходит вовсе.
     */
    id: buildPlatformSchema,
    kind: z.enum(['agent', 'script']),
    file: agentBuildSchema.shape.file,
    sha256: agentBuildSchema.shape.sha256,
    sizeBytes: agentBuildSchema.shape.sizeBytes,
});
/** Состояние каталога выпусков для интерфейса. */
export const agentReleaseSchema = z.object({
    /** `false` — каталог выпусков не задан или не читается. */
    available: z.boolean(),
    version: z.string().nullable(),
    /** Сборки и скрипты установки: то, что показывается кнопками «скачать». */
    assets: z.array(agentAssetSchema),
    notes: z.string(),
    /** Почему выпуска нет: не задан каталог, нет файла, битый манифест. */
    problem: z.string().nullable(),
});
// — Управляющие сообщения (§9.3) ——————————————————————————————
/**
 * Сервер → агент: доступна версия, забирай отсюда.
 *
 * Адрес одноразовый: тикет в нём живёт пять минут и сгорает при первом
 * использовании — тем же порядком, что тикеты VNC и видео (§10.1).
 * Бинарник агента раздаётся без входа в систему, и постоянная ссылка
 * на него означала бы открытый файловый сервер.
 */
export const updateCheckMessageSchema = z.object({
    t: z.literal('update.check'),
    version: agentVersionSchema,
    url: z.string().min(1).max(2000),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    sizeBytes: z.number().int().positive(),
});
/**
 * Агент → сервер: чем кончилось.
 *
 * Успех тоже сообщается, хотя вслед за ним агент завершается и связь
 * рвётся: без этого сообщения «обновился и перезапустился» выглядело бы
 * на платформе точно так же, как «упал при обновлении».
 */
export const updateResultMessageSchema = z.object({
    t: z.literal('update.result'),
    version: agentVersionSchema,
    ok: z.boolean(),
    /** Причина отказа простым языком; `null` при успехе. */
    error: z.string().max(500).nullable().default(null),
});
// — Сравнение версий ————————————————————————————————————
/**
 * Сравнение версий по числам, а не по строке.
 *
 * «1.10.0» и «1.9.0» в лексикографическом порядке встают наоборот, и агент
 * версии 1.10.0 получал бы предложение «обновиться» до 1.9.0 бесконечно.
 *
 * Возвращает −1, 0 или 1.
 *
 * Суффикс отбрасывается: `0.1.0-dev` сравнивается как `0.1.0`, то есть
 * равен выпуску 0.1.0 и обновится до 0.2.0. Это осознанно — предвыпуск
 * с теми же числами не должен подменяться выпуском сам собой, иначе
 * разработчик, поставивший себе сборку из ветки, потерял бы её при первом
 * же подключении.
 *
 * Версия, в которой чисел нет вовсе (`abc1234` из сборки CI), считается
 * самой старой: такой агент обновиться должен, а не остаться навсегда.
 */
export function compareVersions(left, right) {
    const parse = (value) => {
        const match = /^(\d{1,4})\.(\d{1,4})\.(\d{1,4})/.exec(value.trim());
        return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : [-1, -1, -1];
    };
    const a = parse(left);
    const b = parse(right);
    for (let index = 0; index < 3; index += 1) {
        const first = a[index] ?? 0;
        const second = b[index] ?? 0;
        if (first !== second) {
            return first < second ? -1 : 1;
        }
    }
    return 0;
}
/**
 * Нужно ли предлагать обновление.
 *
 * Ровно одно условие: у агента версия строго старее выпуска. Равные версии
 * не обновляются (иначе агент перезапускался бы по кругу), а более новые
 * не откатываются — агент с версией новее платформы означает, что кто-то
 * поставил его руками, и отбирать это у администратора платформа не вправе.
 */
export function needsUpdate(agentVersion, releaseVersion) {
    if (agentVersion === null || agentVersion.length === 0) {
        return false;
    }
    return compareVersions(agentVersion, releaseVersion) < 0;
}
//# sourceMappingURL=agent-update.js.map